import { parseRequestEditor, type RequestEditorState } from "./externalRequest";
import type { DocumentData } from "firebase/firestore";
import { compactData, dateValue, stringValue } from "./firestore";
import {
  FIELD_TYPES,
  UNIVERSAL_ATHLETE_FIELD_ROLES,
  type LeagueAthleteFieldType,
  type LeagueAthleteUniversalFieldRole,
} from "./fieldDefinition";

export type ExternalDataEntityKind = "athletes" | "competitions";
export type ExternalSourceKind = "json" | "googleSheets";
export type LeagueExternalDataSourceStatus = "active" | "archived";
export type ExternalRecordKeyStrategy = "participant-id" | "full-name" | "random";
export type ExternalMappingProvenance = "ai" | "deterministic" | "user";
export type GoogleSheetAccessMode = "public" | "private";
export type GoogleSheetOrientation = "keysInFirstRow" | "keysInFirstColumn";

export const EXTERNAL_VALUE_TRANSFORMS = [
  "identity",
  "string",
  "trim",
  "date-time-to-date",
  "date-from-string",
  "string-boolean-to-boolean",
  "int-from-string-or-number",
  "double-from-string-or-number",
  "value-map",
  "url-string",
  "hex-color",
] as const;

export type ExternalValueTransform = (typeof EXTERNAL_VALUE_TRANSFORMS)[number];

export interface ExternalSourceHeader {
  key: string;
  value: string;
}

export interface ExternalValueMapEntry {
  from: string;
  to: string;
}

export interface ExternalFieldMapping {
  targetFieldID: string;
  targetUniversalRole?: LeagueAthleteUniversalFieldRole;
  targetType: LeagueAthleteFieldType;
  targetLabel: string;
  sourcePath: string;
  fallbackPaths: string[];
  transforms: ExternalValueTransform[];
  sentinelValues: string[];
  valueMap: ExternalValueMapEntry[];
  confidence: number;
  provenance: ExternalMappingProvenance;
}

export interface ExternalFieldMappingProfile {
  profileVersion: number;
  entityKind: ExternalDataEntityKind;
  recordKeyStrategy: ExternalRecordKeyStrategy;
  mappings: ExternalFieldMapping[];
  skippedSourcePaths: string[];
}

export interface LeagueExternalDataSource {
  id: string;
  leagueID: string;
  name: string;
  entityKind: ExternalDataEntityKind;
  sourceKind: ExternalSourceKind;
  endpointURL?: string;
  headers: ExternalSourceHeader[];
  requestEditor?: RequestEditorState;
  recordsPath?: string;
  spreadsheetURL?: string;
  spreadsheetID?: string;
  spreadsheetRange?: string;
  spreadsheetTabName?: string;
  spreadsheetTabID?: number;
  sheetOrientation?: GoogleSheetOrientation;
  accessMode?: GoogleSheetAccessMode;
  mappingProfile: ExternalFieldMappingProfile;
  status: LeagueExternalDataSourceStatus;
  lastFetchedAt?: Date;
  lastRecordCount?: number;
  lastFetchError?: string;
  createdByUserID?: string;
  createdAt?: Date;
  updatedAt?: Date;
  updatedByUserID?: string;
}

export const DEFAULT_SENTINEL_VALUES = ["", "---", "--", "-", "n/a", "N/A", "null", "NULL", "TBD"];

export const EMPTY_MAPPING_PROFILE: ExternalFieldMappingProfile = {
  profileVersion: 1,
  entityKind: "athletes",
  recordKeyStrategy: "participant-id",
  mappings: [],
  skippedSourcePaths: [],
};

export function externalSourceID(leagueID: string, seed = crypto.randomUUID().toLowerCase()): string {
  return `${leagueID}_extsource_${sbotSlug(seed)}`;
}

export function googleSpreadsheetIDFromInput(input: string): string | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  try {
    const url = new URL(trimmed);
    const segments = url.pathname.split("/").filter(Boolean);
    const markerIndex = segments.indexOf("d");
    if (markerIndex >= 0 && segments[markerIndex + 1]) return segments[markerIndex + 1];
  } catch {
    // Raw spreadsheet IDs are accepted below.
  }
  return /^[A-Za-z0-9_-]{20,}$/.test(trimmed) ? trimmed : undefined;
}

export function googleSpreadsheetGIDFromInput(input: string): number | undefined {
  try {
    const url = new URL(input.trim());
    const raw = url.searchParams.get("gid") ?? new URLSearchParams(url.hash.replace(/^#/, "")).get("gid");
    if (!raw) return undefined;
    const value = Number(raw);
    return Number.isInteger(value) && value >= 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

export function parseExternalDataSource(id: string, data: DocumentData): LeagueExternalDataSource | null {
  const storedID = stringValue(data.id);
  const leagueID = stringValue(data.leagueID);
  const name = stringValue(data.name);
  const entityKind = stringValue(data.entityKind) as ExternalDataEntityKind | undefined;
  const sourceKind = parseSourceKind(data.sourceKind);
  const endpointURL = stringValue(data.endpointURL);
  const status = stringValue(data.status) as LeagueExternalDataSourceStatus | undefined;
  const spreadsheetID = stringValue(data.spreadsheetID);
  const spreadsheetURL = stringValue(data.spreadsheetURL);
  const spreadsheetRange = stringValue(data.spreadsheetRange);
  const spreadsheetTabName = stringValue(data.spreadsheetTabName);
  const spreadsheetTabID = typeof data.spreadsheetTabID === "number" && Number.isInteger(data.spreadsheetTabID) ? data.spreadsheetTabID : undefined;
  const sheetOrientation = stringValue(data.sheetOrientation) as GoogleSheetOrientation | undefined;
  const accessMode = stringValue(data.accessMode) as GoogleSheetAccessMode | undefined;
  if (
    storedID !== id ||
    !leagueID ||
    !name ||
    (entityKind !== "athletes" && entityKind !== "competitions") ||
    (status !== "active" && status !== "archived") ||
    (sourceKind === "json" && !endpointURL) ||
    (sourceKind === "googleSheets" &&
      (!spreadsheetID ||
        !spreadsheetRange ||
        (sheetOrientation !== undefined && sheetOrientation !== "keysInFirstRow" && sheetOrientation !== "keysInFirstColumn") ||
        (accessMode !== undefined && accessMode !== "public" && accessMode !== "private")))
  ) {
    return null;
  }
  return {
    id,
    leagueID,
    name,
    entityKind,
    sourceKind,
    endpointURL,
    headers: parseHeaders(data.headers),
    requestEditor: parseRequestEditor(data.requestEditor),
    recordsPath: stringValue(data.recordsPath),
    spreadsheetURL,
    spreadsheetID,
    spreadsheetRange,
    spreadsheetTabName,
    spreadsheetTabID,
    sheetOrientation: sheetOrientation ?? (sourceKind === "googleSheets" ? "keysInFirstRow" : undefined),
    accessMode: accessMode ?? (sourceKind === "googleSheets" ? "public" : undefined),
    mappingProfile: parseMappingProfile(data.mappingProfile),
    status,
    lastFetchedAt: dateValue(data.lastFetchedAt),
    lastRecordCount: typeof data.lastRecordCount === "number" ? data.lastRecordCount : undefined,
    lastFetchError: stringValue(data.lastFetchError),
    createdByUserID: stringValue(data.createdByUserID),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
    updatedByUserID: stringValue(data.updatedByUserID),
  };
}

function parseSourceKind(value: unknown): ExternalSourceKind {
  const string = stringValue(value);
  return string === "googleSheets" ? "googleSheets" : "json";
}

export function externalDataSourceFirestoreData(source: LeagueExternalDataSource): Record<string, unknown> {
  return compactData({
    id: source.id,
    leagueID: source.leagueID,
    name: source.name,
    entityKind: source.entityKind,
    sourceKind: source.sourceKind,
    endpointURL: source.endpointURL,
    headers: source.headers.filter((header) => header.key.trim()).map((header) => ({ key: header.key, value: header.value })),
    requestEditor: source.requestEditor,
    recordsPath: source.recordsPath,
    spreadsheetURL: source.spreadsheetURL,
    spreadsheetID: source.spreadsheetID,
    spreadsheetRange: source.spreadsheetRange,
    spreadsheetTabName: source.spreadsheetTabName,
    spreadsheetTabID: source.spreadsheetTabID,
    sheetOrientation: source.sheetOrientation,
    accessMode: source.accessMode,
    mappingProfile: mappingProfileFirestoreData(source.mappingProfile),
    status: source.status,
    lastFetchedAt: source.lastFetchedAt,
    lastRecordCount: source.lastRecordCount,
    lastFetchError: source.lastFetchError,
    createdByUserID: source.createdByUserID,
    createdAt: source.createdAt,
    updatedAt: source.updatedAt,
    updatedByUserID: source.updatedByUserID,
  });
}

export function mappingProfileFirestoreData(profile: ExternalFieldMappingProfile): Record<string, unknown> {
  return {
    "profile-version": profile.profileVersion,
    "entity-kind": profile.entityKind,
    "record-key-strategy": profile.recordKeyStrategy,
    mappings: profile.mappings.map(mappingFirestoreData),
    "skipped-source-paths": profile.skippedSourcePaths,
  };
}

export function mappingFirestoreData(mapping: ExternalFieldMapping): Record<string, unknown> {
  return compactData({
    "target-field-id": mapping.targetFieldID,
    "target-universal-role": mapping.targetUniversalRole,
    "target-type": mapping.targetType,
    "target-label": mapping.targetLabel,
    "source-path": mapping.sourcePath,
    "fallback-paths": mapping.fallbackPaths,
    transforms: mapping.transforms,
    "sentinel-values": mapping.sentinelValues,
    "value-map": mapping.valueMap.length ? mapping.valueMap.map((entry) => ({ from: entry.from, to: entry.to })) : undefined,
    confidence: mapping.confidence,
    provenance: mapping.provenance,
  });
}

function parseMappingProfile(value: unknown): ExternalFieldMappingProfile {
  const data = asRecord(value);
  const entityKind = stringValue(data?.["entity-kind"]) as ExternalDataEntityKind | undefined;
  const recordKeyStrategy = stringValue(data?.["record-key-strategy"]) as ExternalRecordKeyStrategy | undefined;
  return {
    profileVersion: typeof data?.["profile-version"] === "number" ? data["profile-version"] : 1,
    entityKind: entityKind === "competitions" ? "competitions" : "athletes",
    recordKeyStrategy:
      recordKeyStrategy === "full-name" || recordKeyStrategy === "random" ? recordKeyStrategy : "participant-id",
    mappings: Array.isArray(data?.mappings) ? data.mappings.flatMap(parseMapping) : [],
    skippedSourcePaths: stringArray(data?.["skipped-source-paths"]),
  };
}

function parseMapping(value: unknown): ExternalFieldMapping[] {
  const data = asRecord(value);
  const targetFieldID = stringValue(data?.["target-field-id"]);
  const targetLabel = stringValue(data?.["target-label"]);
  const sourcePath = stringValue(data?.["source-path"]);
  if (!data || !targetFieldID || targetLabel === undefined || !sourcePath) return [];
  const targetType = stringValue(data["target-type"]) as LeagueAthleteFieldType | undefined;
  const role = stringValue(data["target-universal-role"]) as LeagueAthleteUniversalFieldRole | undefined;
  return [
    {
      targetFieldID,
      targetUniversalRole: role && UNIVERSAL_ATHLETE_FIELD_ROLES.includes(role) ? role : undefined,
      targetType: targetType && FIELD_TYPES.includes(targetType) ? targetType : "text",
      targetLabel,
      sourcePath,
      fallbackPaths: stringArray(data["fallback-paths"]),
      transforms: stringArray(data.transforms).filter((entry): entry is ExternalValueTransform =>
        EXTERNAL_VALUE_TRANSFORMS.includes(entry as ExternalValueTransform),
      ),
      sentinelValues: stringArray(data["sentinel-values"]).length ? stringArray(data["sentinel-values"]) : DEFAULT_SENTINEL_VALUES,
      valueMap: parseValueMap(data["value-map"]),
      confidence: clampNumber(data.confidence, 1),
      provenance: parseProvenance(data.provenance),
    },
  ];
}

function parseHeaders(value: unknown): ExternalSourceHeader[] {
  return Array.isArray(value)
    ? value.flatMap((entry) => {
        const data = asRecord(entry);
        const key = stringValue(data?.key);
        if (!key) return [];
        return [{ key, value: typeof data?.value === "string" ? data.value : "" }];
      })
    : [];
}

function parseValueMap(value: unknown): ExternalValueMapEntry[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      const data = asRecord(entry);
      const from = typeof data?.from === "string" ? data.from : undefined;
      const to = typeof data?.to === "string" ? data.to : undefined;
      return from !== undefined && to !== undefined ? [{ from, to }] : [];
    });
  }
  const data = asRecord(value);
  return data
    ? Object.entries(data)
        .filter((entry): entry is [string, string] => typeof entry[1] === "string")
        .map(([from, to]) => ({ from, to }))
        .sort((first, second) => first.from.localeCompare(second.from))
    : [];
}

function parseProvenance(value: unknown): ExternalMappingProvenance {
  return value === "ai" || value === "deterministic" || value === "user" ? value : "user";
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

function clampNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : fallback;
}

function sbotSlug(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join("-");
}
